import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import EditableImage from "./EditableImage";

const TABS = ["Apariencia", "Imágenes", "Secciones", "Datos y contacto", "SEO y publicación"];

function clone(value) {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value));
}

function normalizePublicStatus(value) {
  const candidate = String(value || "").trim().toLowerCase();
  return ["draft", "published", "maintenance"].includes(candidate)
    ? candidate
    : "draft";
}

function publicStatusLabel(value) {
  const status = normalizePublicStatus(value);
  if (status === "published") return "Publicado";
  if (status === "maintenance") return "Mantenimiento";
  return "Borrador";
}

function withoutPublicStatus(value) {
  const next = clone(value || {});
  delete next.publicStatus;
  return next;
}

function statusConfirmationMessage(status) {
  if (status === "published") {
    return "Se guardará el borrador pendiente y se publicará su última revisión real. La web pública pasará a mostrar esa versión.";
  }
  if (status === "maintenance") {
    return "La web pública mostrará la página de mantenimiento usando la última versión publicada. El borrador y el historial no se perderán.";
  }
  return "La web pública quedará oculta como borrador. La última versión publicada y todo el historial se conservarán.";
}

function getAtPath(source, path, fallback = "") {
  return String(path || "")
    .split(".")
    .filter(Boolean)
    .reduce((value, key) => value?.[/^\d+$/.test(key) ? Number(key) : key], source) ?? fallback;
}

function setAtPath(source, path, value) {
  const next = clone(source);
  const keys = String(path || "").split(".").filter(Boolean);
  let cursor = next;
  keys.forEach((rawKey, index) => {
    const key = /^\d+$/.test(rawKey) ? Number(rawKey) : rawKey;
    if (index === keys.length - 1) {
      cursor[key] = value;
      return;
    }
    const nextKey = /^\d+$/.test(keys[index + 1]) ? Number(keys[index + 1]) : keys[index + 1];
    if (cursor[key] === undefined || cursor[key] === null) cursor[key] = typeof nextKey === "number" ? [] : {};
    cursor = cursor[key];
  });
  return next;
}

function Field({ form, path, label, onChange, multiline = false, minRows = 1, type = "text", helperText = "" }) {
  return (
    <TextField
      fullWidth
      type={type}
      label={label}
      value={getAtPath(form, path, "")}
      onChange={(event) => onChange(path, event.target.value)}
      multiline={multiline}
      minRows={multiline ? minRows : undefined}
      helperText={helperText}
    />
  );
}

function ColorField({ form, path, label, onChange }) {
  const value = getAtPath(form, path, "#000000") || "#000000";
  return (
    <Stack direction="row" spacing={1.2} alignItems="center">
      <Box component="input" type="color" value={value} onChange={(event) => onChange(path, event.target.value)} sx={{ width: 48, height: 44, p: 0, border: 0, bgcolor: "transparent", cursor: "pointer" }} />
      <TextField fullWidth label={label} value={value} onChange={(event) => onChange(path, event.target.value)} />
    </Stack>
  );
}

function SectionToggle({ form, path, label, onChange, disabled = false }) {
  return (
    <FormControlLabel
      control={<Switch disabled={disabled} checked={getAtPath(form, path, true) !== false} onChange={(event) => onChange(path, event.target.checked)} />}
      label={label}
    />
  );
}

export default function LandingSettingsDialog({
  open,
  form,
  productsEnabled = true,
  saving = false,
  changingPublicStatus = false,
  initialTab = 0,
  showTrialGuide = false,
  onClose,
  onSave,
  onChangePublicStatus,
  onUploadAsset,
  onDeleteAsset,
}) {
  const [draft, setDraft] = React.useState(() => clone(form || {}));
  const [tab, setTab] = React.useState(initialTab);
  const [error, setError] = React.useState("");
  const [pendingPublicStatus, setPendingPublicStatus] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setDraft(clone(form || {}));
      setTab(Number(initialTab) || 0);
      setError("");
      setPendingPublicStatus("");
    }
  }, [form, initialTab, open]);

  const change = (path, value) => setDraft((current) => setAtPath(current, path, value));

  const upload = async (path, file, metadata, options = {}) => {
    const asset = await onUploadAsset?.(file, metadata, options);
    const value = asset?.value || asset?.publicPath || asset?.url || "";
    if (!value) throw new Error("No se recibió la ruta del archivo subido");
    change(path, value);
  };

  const remove = async (path) => {
    const current = getAtPath(draft, path, "");
    if (current) await onDeleteAsset?.(current);
    change(path, "");
  };

  const currentPublicStatus = normalizePublicStatus(form?.publicStatus);
  const selectedPublicStatus = normalizePublicStatus(draft?.publicStatus);
  const publicStatusChanged = selectedPublicStatus !== currentPublicStatus;
  const contentChanged = React.useMemo(
    () =>
      JSON.stringify(withoutPublicStatus(draft)) !==
      JSON.stringify(withoutPublicStatus(form)),
    [draft, form]
  );
  const busy = saving || changingPublicStatus;

  const persist = async (nextPublicStatus = "") => {
    try {
      setError("");

      if (contentChanged) {
        await onSave?.(
          { ...draft, publicStatus: currentPublicStatus },
          {
            editorKey: `settings:${tab}`,
            editorType: "settings",
            tabIndex: tab,
            sectionLabel: TABS[tab] || "Ajustes globales",
          }
        );
      }

      if (nextPublicStatus) {
        const result = await onChangePublicStatus?.(nextPublicStatus);
        if (!result) {
          throw new Error("No se pudo confirmar el cambio de estado público.");
        }
      }

      setPendingPublicStatus("");
      onClose?.();
    } catch (err) {
      setError(err?.message || "No se pudieron guardar los ajustes");
    }
  };

  const save = async () => {
    if (publicStatusChanged) {
      setPendingPublicStatus(selectedPublicStatus);
      return;
    }
    await persist();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pb: 0, fontWeight: 950, color: "#19242F" }}>Configuración de la página</DialogTitle>
      <Box sx={{ px: 2.5, pt: 1 }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto">
          {TABS.map((label) => <Tab key={label} label={label} sx={{ textTransform: "none", fontWeight: 850 }} />)}
        </Tabs>
      </Box>
      <DialogContent dividers>
        <Stack spacing={2.2}>
          {showTrialGuide ? (
            <Alert severity="info">Completá primero identidad, portada, contacto y publicación. Las imágenes avanzadas pueden agregarse después.</Alert>
          ) : null}

          {tab === 0 ? (
            <Stack spacing={2}>
              <Typography sx={{ fontWeight: 950 }}>Paleta general</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.primaryColor" label="Color principal" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.secondaryColor" label="Color oscuro" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.surfaceColor" label="Superficie" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.backgroundColor" label="Fondo general" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.textColor" label="Texto principal" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><ColorField form={draft} path="theme.mutedTextColor" label="Texto secundario" onChange={change} /></Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}><Field form={draft} path="theme.headingFontFamily" label="Tipografía de títulos" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><Field form={draft} path="theme.bodyFontFamily" label="Tipografía de textos" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><Field form={draft} path="theme.googleFontsUrl" label="URL de Google Fonts" onChange={change} /></Grid>
                <Grid item xs={12} sm={6}><Field form={draft} path="theme.borderRadius" label="Redondeo general" type="number" onChange={(path, value) => change(path, Number(value) || 0)} /></Grid>
              </Grid>
            </Stack>
          ) : null}

          {tab === 1 ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "theme.logoUrl", "")}
                  label="Logo principal"
                  helperText="SVG, PNG, WebP o JPG."
                  disabled={saving}
                  onUpload={(file, options) => upload("theme.logoUrl", file, { sectionKey: "branding", assetKey: "logo" }, options)}
                  onRemove={() => remove("theme.logoUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "theme.compactLogoUrl", "")}
                  label="Logo compacto"
                  helperText="Versión cuadrada o reducida para celular."
                  disabled={saving}
                  onUpload={(file, options) => upload("theme.compactLogoUrl", file, { sectionKey: "branding", assetKey: "compact-logo" }, options)}
                  onRemove={() => remove("theme.compactLogoUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "theme.faviconUrl", "")}
                  label="Favicon"
                  helperText="Ícono cuadrado para la pestaña."
                  disabled={saving}
                  accept="image/svg+xml,image/png,image/webp,image/x-icon,.svg,.ico"
                  onUpload={(file, options) => upload("theme.faviconUrl", file, { sectionKey: "branding", assetKey: "favicon" }, options)}
                  onRemove={() => remove("theme.faviconUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "hero.imageUrl", "")}
                  label="Imagen principal de portada"
                  helperText="Se usa cuando la portada no tiene slider."
                  disabled={saving}
                  onUpload={(file, options) => upload("hero.imageUrl", file, { sectionKey: "hero", assetKey: "background" }, options)}
                  onRemove={() => remove("hero.imageUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "about.imageUrl", "")}
                  label="Imagen sobre el negocio"
                  disabled={saving}
                  onUpload={(file, options) => upload("about.imageUrl", file, { sectionKey: "about", assetKey: "main" }, options)}
                  onRemove={() => remove("about.imageUrl")}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableImage
                  value={getAtPath(draft, "seo.imageUrl", "")}
                  label="Imagen para compartir"
                  helperText="Vista previa para redes sociales y mensajería."
                  disabled={saving}
                  onUpload={(file, options) => upload("seo.imageUrl", file, { sectionKey: "seo", assetKey: "social" }, options)}
                  onRemove={() => remove("seo.imageUrl")}
                />
              </Grid>
            </Grid>
          ) : null}

          {tab === 2 ? (
            <Stack spacing={0.2}>
              <SectionToggle form={draft} path="sections.showNavbar" label="Navegación" onChange={change} />
              <SectionToggle form={draft} path="sections.showHero" label="Portada principal" onChange={change} />
              <SectionToggle form={draft} path="sections.showSpotlight" label="Banner destacado" onChange={change} disabled={!productsEnabled} />
              <SectionToggle form={draft} path="sections.showProducts" label="Productos o servicios" onChange={change} disabled={!productsEnabled} />
              <SectionToggle form={draft} path="sections.showFeatured" label="Productos destacados" onChange={change} disabled={!productsEnabled} />
              <SectionToggle form={draft} path="sections.showCategories" label="Categorías" onChange={change} disabled={!productsEnabled} />
              <SectionToggle form={draft} path="sections.showBrands" label="Marcas" onChange={change} disabled={!productsEnabled} />
              <SectionToggle form={draft} path="sections.showAbout" label="Sobre el negocio" onChange={change} />
              <SectionToggle form={draft} path="sections.showHighlights" label="Diferenciales" onChange={change} />
              <SectionToggle form={draft} path="sections.showContact" label="Contacto" onChange={change} />
              <SectionToggle form={draft} path="sections.showFloatingWhatsapp" label="WhatsApp flotante" onChange={change} />
              <SectionToggle form={draft} path="sections.showFooter" label="Pie de página" onChange={change} />
              {!productsEnabled ? <Alert severity="info" sx={{ mt: 1.5 }}>Las secciones comerciales quedan desactivadas porque el proyecto no tiene habilitado el módulo de productos.</Alert> : null}
            </Stack>
          ) : null}

          {tab === 3 ? (
            <Grid container spacing={2}>
              <Grid item xs={12}><Field form={draft} path="business.name" label="Nombre del negocio" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.shortDescription" label="Descripción breve" onChange={change} multiline minRows={3} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.whatsapp" label="WhatsApp" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.phone" label="Teléfono" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.email" label="Correo electrónico" type="email" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.address" label="Dirección" onChange={change} /></Grid>
              <Grid item xs={12} sm={4}><Field form={draft} path="business.city" label="Ciudad" onChange={change} /></Grid>
              <Grid item xs={12} sm={4}><Field form={draft} path="business.province" label="Provincia" onChange={change} /></Grid>
              <Grid item xs={12} sm={4}><Field form={draft} path="business.country" label="País" onChange={change} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.schedule" label="Horarios" onChange={change} multiline minRows={2} /></Grid>
              <Grid item xs={12}><Field form={draft} path="business.mapEmbedUrl" label="URL de mapa embebido" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.instagram" label="Instagram" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.facebook" label="Facebook" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.linkedin" label="LinkedIn" onChange={change} /></Grid>
              <Grid item xs={12} sm={6}><Field form={draft} path="business.socials.website" label="Sitio externo" onChange={change} /></Grid>
            </Grid>
          ) : null}

          {tab === 4 ? (
            <Stack spacing={2}>
              <FormControl fullWidth disabled={busy}>
                <InputLabel>Estado público</InputLabel>
                <Select
                  label="Estado público"
                  value={selectedPublicStatus}
                  onChange={(event) => {
                    change("publicStatus", event.target.value);
                    setPendingPublicStatus("");
                  }}
                  inputProps={{ "aria-describedby": "public-status-help" }}
                >
                  <MenuItem value="draft">Borrador</MenuItem>
                  <MenuItem value="published">Publicado</MenuItem>
                  <MenuItem value="maintenance">Mantenimiento</MenuItem>
                </Select>
              </FormControl>
              <Typography id="public-status-help" variant="body2" sx={{ color: "text.secondary" }}>
                Estado actual del servidor: <strong>{publicStatusLabel(currentPublicStatus)}</strong>.
                La selección no modifica la web hasta que confirmes la transición.
              </Typography>
              <Field form={draft} path="seo.title" label="Título para Google y redes" onChange={change} />
              <Field form={draft} path="seo.description" label="Descripción para Google y redes" onChange={change} multiline minRows={4} />
              <Field form={draft} path="messages.maintenanceTitle" label="Título de mantenimiento" onChange={change} />
              <Field form={draft} path="messages.maintenanceDescription" label="Mensaje de mantenimiento" onChange={change} multiline minRows={3} />
              <Alert severity={selectedPublicStatus === "published" ? "success" : "warning"}>
                {selectedPublicStatus === "published"
                  ? "Publicado toma siempre el último borrador guardado y crea una revisión pública real."
                  : selectedPublicStatus === "maintenance"
                    ? "Mantenimiento conserva la última publicación y muestra solamente el aviso público."
                    : "Borrador oculta la web pública sin borrar publicaciones ni revisiones anteriores."}
              </Alert>
              {pendingPublicStatus ? (
                <Alert severity="info" role="alert">
                  <Typography sx={{ fontWeight: 900 }}>
                    Confirmar cambio a {publicStatusLabel(pendingPublicStatus)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {statusConfirmationMessage(pendingPublicStatus)}
                  </Typography>
                </Alert>
              ) : null}
            </Stack>
          ) : null}

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {pendingPublicStatus ? (
          <>
            <Button
              onClick={() => setPendingPublicStatus("")}
              disabled={busy}
              sx={{ textTransform: "none", fontWeight: 850 }}
            >
              Volver
            </Button>
            <Button
              variant="contained"
              onClick={() => persist(pendingPublicStatus)}
              disabled={busy}
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, bgcolor: "#F35A3A" }}
            >
              {busy ? "Aplicando…" : `Confirmar ${publicStatusLabel(pendingPublicStatus)}`}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy} sx={{ textTransform: "none", fontWeight: 850 }}>Cancelar</Button>
            <Button variant="contained" onClick={save} disabled={busy} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, bgcolor: "#F35A3A" }}>
              {busy ? "Guardando…" : publicStatusChanged ? "Revisar transición" : "Guardar configuración"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
